import { useContext, useEffect, useMemo, useState } from "react";
import { NextPage } from "next";
import { Box, Flex, Show, Text } from "@chakra-ui/react";

// Components
import Journey from "components/AboutUs/Journey";
import StaderRoadMap from "components/AboutUs/RoadMap";

// providers
import { PageProvider } from "providers/PageProvider";
import { PageContext, Footer, Header, useStaderTVL, BreadCrum } from "mlp-sdk";
// Utils
import { fetchPageData, prepareNavData } from "mlp-sdk/utils/common";
import { trackEvent } from "mlp-sdk/utils/firebase";

import { LP_ABOUT_PAGE } from "mlp-sdk/constants/firebase";
import navData from "@constants/navData.json";
import PageHead from "@components/PageHead/PageHead";

const AboutStader = () => {
  const { page } = useContext(PageContext);

  const [isBannerScrolled, setIsBannerScrolled] = useState(false);

  const { tvl } = useStaderTVL();

  const { navData, networks } = useMemo(
    () => prepareNavData(page.data.navData, tvl),
    [page.data.navData, tvl]
  );
  const pageData = page.data.pageData;

  const pageDetails = pageData.section_details;

  useEffect(() => {
    trackEvent(LP_ABOUT_PAGE);
  }, []);

  useEffect(() => {
    const heroSection = document.getElementById("partnersHeroSection");
    const observer = new IntersectionObserver(
      (entries) => {
        for (let entry of entries) {
          if (entry.isIntersecting) {
            setIsBannerScrolled(true);
          } else {
            setIsBannerScrolled(false);
          }
        }
      },
      {
        threshold: 1,
      }
    );
    heroSection && observer.observe(heroSection);

    // cleanup function
    return () => {
      heroSection && observer.unobserve(heroSection);
    };
  }, []);

  return (
    <>
      <PageHead page="about-stader" />
      <Header
        data={navData}
        networks={networks}
        bgColor={isBannerScrolled ? "transparent" : undefined}
        tvl={tvl}
      />

      {/* // TODO Temporary solution by margin (as position changed to sticky) height should be dynamic */}
      <Box
        id="partnersHeroSection"
        className="aboutBanner"
        mt={{ base: 0, lg: "-86px" }}
      >
        <Show below="lg">
          <BreadCrum />
        </Show>

        <Flex
          mt={{ base: "72px", lg: "40px" }}
          h="calc(100% - 72px)"
          w="100%"
          gap="24px"
          flexDir="column"
          alignItems="center"
          justifyContent={{ base: "flex-end", md: "center" }}
          textAlign="center"
          pb={{ base: "64px", md: "0" }}
        >
          <Show above="lg">
            <Box w="100%" maxW="1192px">
              <BreadCrum ml={0} />
            </Box>
          </Show>
          <Text
            fontWeight="700"
            fontSize={{ base: "32px", lg: "64px" }}
            lineHeight={{ base: "38px", lg: "76px" }}
          >
            {pageDetails?.title || ""}
          </Text>
          <Text
            textStyle={{ base: "p12", md: "p16" }}
            fontWeight="500"
            color="textSecondary"
            maxW={{ base: "328px", lg: "584px" }}
          >
            {pageDetails?.sub_title || ""}
          </Text>
        </Flex>
      </Box>

      <Journey />

      <StaderRoadMap />

      <Footer data={navData} />
    </>
  );
};

const AboutUsPage: NextPage = ({ page }: any) => {
  return (
    <PageProvider page={page}>
      <AboutStader />
    </PageProvider>
  );
};

export async function getStaticProps() {
  const [pageRes] = await Promise.all([fetchPageData("landing-about-page")]);

  const pageData = pageRes.data?.attributes || null;

  const pageError = pageRes.error || null;

  return {
    props: {
      page: {
        data: {
          navData,
          pageData: pageData,
        },
        error: { navError: null, pageError },
      },
    },
  };
}

export default AboutUsPage;
